'use client';

import { Shield, ShieldAlert, ShieldOff } from 'lucide-react';
import type { useWalletCapabilities } from '@/hooks/use-eip7702';

/**
 * Shared rendering for the Deposit-Protection status indicator. Extracted
 * from `PortfolioDropdown` so the manage-modal footer (the new sole entry
 * point) and any other surface read the exact same On / Off / Wallet /
 * Loading semantics.
 */

type Capability = ReturnType<typeof useWalletCapabilities>;
type StatusLike = { status: 'off' | 'active' } | undefined;

export type ProtectionUiState = 'loading' | 'incompatible' | 'active' | 'off';

/**
 * Collapse the (status, capability, loading) triple into a single state.
 * Order matters: loading dominates so nothing flashes "Off" during a
 * refetch, and an incompatible wallet makes any stored status moot.
 */
export function protectionUiState(
  status: StatusLike,
  capability: Capability,
  isLoading: boolean,
): ProtectionUiState {
  if (isLoading || capability.isLoading) return 'loading';
  if (capability.result && !capability.result.supports7702) return 'incompatible';
  if (status?.status === 'active') return 'active';
  return 'off';
}

export function ProtectionStatusIcon({ state, className = 'h-3.5 w-3.5' }: { state: ProtectionUiState; className?: string }) {
  if (state === 'loading') return <Shield className={`${className} shrink-0 text-zinc-500`} />;
  if (state === 'incompatible') return <ShieldAlert className={`${className} shrink-0 text-amber-300`} />;
  if (state === 'active') return <Shield className={`${className} shrink-0 text-emerald-300`} />;
  return <ShieldOff className={`${className} shrink-0 text-zinc-500`} />;
}

/** Right-aligned status pill. "Wallet" is shorthand for "wallet not supported". */
export function ProtectionStatusPill({ state }: { state: ProtectionUiState }) {
  const base =
    'text-[9px] font-mono tracking-[0.18em] uppercase px-1.5 py-0.5 rounded-[3px] border';
  if (state === 'loading') {
    return <span className={`${base} bg-transparent border-white/10 text-zinc-500`}>…</span>;
  }
  if (state === 'incompatible') {
    return <span className={`${base} bg-amber-500/10 border-amber-400/40 text-amber-200`}>Wallet</span>;
  }
  if (state === 'active') {
    return <span className={`${base} bg-emerald-500/15 border-emerald-400/40 text-emerald-200`}>On</span>;
  }
  return <span className={`${base} bg-transparent border-white/10 text-zinc-500`}>Off</span>;
}
